import {
  BookOpenTextIcon,
  CardsIcon,
  FilesIcon,
  GraphIcon,
  PhoneCallIcon,
  QuotesIcon,
  ScissorsIcon,
  ScrollIcon,
  SlackLogoIcon,
  TrophyIcon,
  type Icon,
} from '@phosphor-icons/react';

import {
  buildToolFriendlyNamesMap as buildToolFriendlyNamesMapBase,
  type ToolFriendlyNamesMap,
  type ToolMessages,
  type ToolRegistry,
} from 'modules/shared/libs/chat/utils/tool-calling-utils';

import type { HatchTool } from '../types';
import type { Source } from 'modules/hatch/api/sources.api.types';

const ENTITY_GRAPH_TOOL_MESSAGES: ToolMessages = {
  preparing: 'Preparing to analyze company relationships',
  running: 'Mapping company relationships',
  complete: 'Company relationships mapped and retrieved',
  transition: "Based on your ask, I'll analyze the competitive landscape",
};

export const HATCH_TOOL_REGISTRY: ToolRegistry = {
  displayNames: {
    curated_snippets_search: 'Curated Snippets',
    entity_graph_get: 'Entity Graph',
    evidence_quotes_search: 'Evidence & Quotes',
    get_entity_graph_tool: 'Entity Graph',
    gong_call_transcripts_search: 'Gong',
    load_capability: 'Skill',
    markdown_source_search: 'Documents',
    normalized_call_transcripts_search: 'Call Transcripts',
    slack_messages_search: 'Slack Messages',
    stak_cards_aggregate: 'Insights',
    stak_cards_search: 'Insights',
    win_loss_interview_report_search: 'Win-Loss Reports',
    win_loss_transcript_full_search: 'Win-Loss Transcripts',
  } satisfies Record<HatchTool, string>,
  icons: {
    curated_snippets_search: ScissorsIcon,
    entity_graph_get: GraphIcon,
    evidence_quotes_search: QuotesIcon,
    get_entity_graph_tool: GraphIcon,
    gong_call_transcripts_search: PhoneCallIcon,
    load_capability: ScrollIcon,
    markdown_source_search: FilesIcon,
    normalized_call_transcripts_search: PhoneCallIcon,
    slack_messages_search: SlackLogoIcon,
    stak_cards_aggregate: CardsIcon,
    stak_cards_search: CardsIcon,
    win_loss_interview_report_search: TrophyIcon,
    win_loss_transcript_full_search: BookOpenTextIcon,
  } satisfies Record<HatchTool, Icon>,
  messages: {
    curated_snippets_search: {
      preparing: 'Preparing curated snippets search',
      running: 'Searching expert-curated knowledge',
      complete: 'Curated snippets retrieved',
    },
    entity_graph_get: ENTITY_GRAPH_TOOL_MESSAGES,
    evidence_quotes_search: {
      preparing: 'Preparing evidence search',
      running: 'Searching for supporting evidence and quotes',
      complete: 'Evidence and quotes retrieved',
    },
    get_entity_graph_tool: ENTITY_GRAPH_TOOL_MESSAGES,
    gong_call_transcripts_search: {
      preparing: 'Searching call transcripts',
      running: 'Checking recent calls',
      complete: 'Finished searching Gong calls',
    },
    // A skill is instructions the agent loads, so it reads "Loaded", not "complete".
    load_capability: {
      preparing: 'Loading {name}',
      running: 'Loading {name}',
      complete: 'Loaded {name}',
    },
    markdown_source_search: {
      preparing: 'Preparing document search',
      running: 'Searching documents',
      complete: 'Documents retrieved',
    },
    normalized_call_transcripts_search: {
      preparing: 'Preparing call transcripts search',
      running: 'Searching call transcripts',
      complete: 'Call transcripts retrieved',
    },
    slack_messages_search: {
      preparing: 'Preparing Slack search',
      running: 'Searching Slack messages',
      complete: 'Slack messages retrieved',
    },
    stak_cards_aggregate: {
      preparing: 'Preparing insights search',
      running: 'Searching for insights',
      complete: 'Insights found',
    },
    stak_cards_search: {
      preparing: 'Preparing insights retrieval',
      running: 'Gathering insights',
      complete: 'Insights retrieved',
    },
    win_loss_interview_report_search: {
      preparing: 'Preparing Win-Loss reports search',
      running: 'Searching Win-Loss interview reports',
      complete: 'Win-Loss reports retrieved',
    },
    win_loss_transcript_full_search: {
      preparing: 'Preparing Win-Loss transcripts search',
      running: 'Searching Win-Loss transcripts',
      complete: 'Win-Loss transcripts retrieved',
    },
  } satisfies Record<HatchTool, ToolMessages>,
};

export const buildToolFriendlyNamesMap = (
  sources: Source[],
): ToolFriendlyNamesMap =>
  buildToolFriendlyNamesMapBase(sources, HATCH_TOOL_REGISTRY);
